import { useMutation } from "@tanstack/react-query";

import {
  productDetailQuery,
  productDetailRecommendQuery,
  productVariantQuery,
  productsByIdsQuery,
  productsQuery,
} from "../graphql/product-gql";
import { useCacheFirstRequest } from "../graphql/queryClient";
import { locale } from "../locales";
import type {
  IProductDetailQuery,
  IProductDetailRecommendQuery,
  IProductVariantQuery,
  IProductsByIdsQuery,
  IProductsQuery,
} from "../types/api/product";
import { ToastType } from "../types/enum";
import { IQueryProps } from "../types/queries";

export default function useProductQuery({ lang, openToast }: IQueryProps) {
  const request = useCacheFirstRequest();

  /** 상품 상세 조회 */
  const productMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) => request<IProductDetailQuery>("product-detail", productDetailQuery, variables),
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  /** 상품 상세 추천 상품 조회 */
  const recommendMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      request<IProductDetailRecommendQuery>("product-recommend", productDetailRecommendQuery, variables),
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  /** 상품 옵션(variant) 조회 */
  const productVariantMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) => request<IProductVariantQuery>("product-variant", productVariantQuery, variables),
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  /** 상품 목록 조회 */
  const productsMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) => request<IProductsQuery>("products", productsQuery, variables),
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  /** 상품 GID 목록으로 조회 */
  const productsByIdsMutation = useMutation({
    mutationFn: (variables: { ids: string[] }) => request<IProductsByIdsQuery>("products-by-ids", productsByIdsQuery, variables),
    onError: (error) => {
      openToast({ message: locale(lang).server, type: ToastType.Error });
      throw new Error(error.message);
    },
  });

  return {
    fetchProduct: productMutation.mutateAsync,
    productData: productMutation.data,
    productLoading: productMutation.isPending,
    fetchRecommend: recommendMutation.mutateAsync,
    recommendData: recommendMutation.data,
    recommendLoading: recommendMutation.isPending,
    fetchProductVariant: productVariantMutation.mutateAsync,
    productVariantData: productVariantMutation.data,
    productVariantLoading: productVariantMutation.isPending,
    fetchProducts: productsMutation.mutateAsync,
    productsData: productsMutation.data,
    productsLoading: productsMutation.isPending,
    fetchProductsByIds: productsByIdsMutation.mutateAsync,
  };
}
